import React from 'react';
import { IUser } from '../api/base/Auth/types';
import { USER_ROLES, USER_STATUS } from './types/user';
import { APP_ROUTES } from '../routing/AppRoutes';
import format from 'date-fns/format';

export const getArrayWithoutElementAtIndex = (
  arr: any[],
  index: number,
): any[] => {
  return arr.filter((el, currentIndex) => currentIndex !== index);
};

export const capitalizeFirstLetter = (str: string): string => {
  return str && `${str.charAt && str.charAt(0)}${str.slice(1)}`;
};

export function lazily(loader: () => Promise<any>): any {
  return new window.Proxy(
    {},
    {
      // componentName is a property of target object
      // if we want to get any property from this empty object - we call this function
      // and return our component
      get: (target, componentName) =>
        React.lazy(() =>
          loader().then((response: any) => ({
            default: response[componentName],
          })),
        ),
    },
  );
}

export const getUserHomePage = (user: IUser): string => {
  if (user.status === USER_STATUS.WAIT_FOR_APPROVING) {
    return user.role === USER_ROLES.JOURNALIST
      ? APP_ROUTES.JOURNALIST_PERSONAL_INFO
      : APP_ROUTES.EXPERT_PERSONAL_INFO;
  }
  if (user.status === USER_STATUS.MUST_ADD_GUEST) {
    return APP_ROUTES.REG_AS_EXPERT2;
  }
  if (user.status === USER_STATUS.MUST_PAY) {
    return APP_ROUTES.REG_AS_EXPERT3;
  }
  if (user.status === USER_STATUS.PAYMENT_IN_PROCESS) {
    return APP_ROUTES.EXPERT_PAYMENT;
  }
  if (user.status === USER_STATUS.WILL_BE_DELETED) {
    switch (user.role) {
      case USER_ROLES.JOURNALIST:
        return APP_ROUTES.JOURNALIST_PERSONAL_INFO;
      case USER_ROLES.EXPERT:
        return APP_ROUTES.EXPERT_PERSONAL_INFO;
      default:
        return APP_ROUTES.MAIN;
    }
  } else {
    switch (user.role) {
      case USER_ROLES.JOURNALIST:
        return APP_ROUTES.JOURNALIST_PERSONAL_INFO;
      case USER_ROLES.EXPERT:
        return APP_ROUTES.EXPERT_PERSONAL_INFO;
      default:
        return APP_ROUTES.MAIN;
    }
  }
};

export const getArrWithNewEl = (arr: Array<any>, id: number): Array<any> => {
  const foundIndex = arr.findIndex((el) => el === id);
  return foundIndex === -1
    ? [...arr, id]
    : getArrayWithoutElementAtIndex(arr, foundIndex);
};

export const debounce = (
  callback: (...data: any) => any,
  timeout: number,
): any => {
  let timeoutId: any = null;

  return (...d: any[]): void => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      callback(...d);
    }, timeout);
  };
};

export const getFullName = (someUser: {
  first_name: string;
  last_name: string;
}): string => {
  return `${someUser.first_name} ${someUser.last_name}`;
};

const checkDate = (callback: () => string) => {
  try {
    return callback();
  } catch (err) {
    return '-';
  }
};

export const getReadableFullDate = (date: string | Date): string =>
  checkDate(() => format(new Date(date), 'y/MM/dd hh:mm aaa'));

export const getReadableDate = (date: string | Date): string =>
  checkDate(() => format(new Date(date), 'Y-MM-dd'));

export const getReadableTime = (date: string | Date): string =>
  checkDate(() => format(new Date(date), 'hh:mm aaa'));

export const isEditingNewsForm = (pathName: string): boolean => {
  return pathName.includes('/contacts-manager/expert/news/edit');
};

export const isEditingEventsForm = (pathName: string): boolean => {
  return pathName.includes('/events/event/edit');
};

export function checkIsImage(url: string) {
  return /\.(jpg|jpeg|png|webp|avif|gif|svg)$/.test(url);
}

export function msToTime(duration: number) {
  const minutes = Math.floor((duration / (1000 * 60)) % 60);
  const hours = Math.floor(duration / (1000 * 60 * 60));
  const days = Number((hours / 24).toFixed(0));

  const readableHours = hours < 10 ? `0${hours}` : hours;
  const readableMinutes = minutes < 10 ? `0${minutes}` : minutes;
  const readableDays = days < 10 ? `0${days}` : days;

  return {
    readableHours,
    readableMinutes,
    readableDays,
  };
}
